import { CloseCircleFilled, SearchOutlined } from '@ant-design/icons';
import { Keyboard } from '@capacitor/keyboard';
import { useRequest } from 'ahooks';
import { Badge } from 'components/ui/badge';
import { Button } from 'components/ui/button';
import { Input } from 'components/ui/input';
import { Skeleton } from 'components/ui/skeleton';
import posthog from 'posthog-js';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { createSearchParams, useNavigate, useSearchParams } from 'react-router-dom';
import ApiContentManager from 'services/api/ApiContentManager';
import { contentActions } from 'store/content';
import { selectSuggestedPosts } from 'store/content/selectors';
import onArticleSelected from 'utils/ContentUtils';
import Platform from 'utils/Platform';
import PostsList from './PostsList';

type ArticlesListProps = {
  /**
   * When true, allows the list page to switch to a grid view.
   * Currently unused here but kept for backward compatibility.
   */
  allowGridView?: boolean;
  /**
   * Optional maximum number of posts to display.
   * When provided, only the first `limitPosts` posts will be shown.
   */
  limitPosts?: number;
};

export default function ArticlesList({ allowGridView, limitPosts }: ArticlesListProps) {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();
  const dispatch = useDispatch();
  const suggestedPosts = useSelector(selectSuggestedPosts);
  const [searchValue, setSearchValue] = useState(searchParams.get('search') || '');

  const { data: tags, loading: isLoadingTags } = useRequest(() => ApiContentManager.getTags(), {
    cacheKey: 'content.tags',
    cacheTime: 1000 * 60 * 10, // 10 minutes
  });

  const { data: recentlyReadPosts, loading: isLoadingRecentlyReadPosts } = useRequest(
    () => ApiContentManager.getRecentlyReadPosts(),
    {
      cacheKey: 'content.recentlyReadPosts',
      cacheTime: 1000 * 60 * 10, // 10 minutes
      debounceWait: 500,
      debounceLeading: true,
    },
  );

  const {
    run: searchPosts,
    data: searchResults,
    loading: isLoadingSearchResults,
  } = useRequest((search?: string, searchedTags?: string) => ApiContentManager.searchPosts(search, searchedTags), {
    manual: true,
    cacheKey: `content.searchResults:${searchParams.get('search') || ''}:${searchParams.get('tags') || ''}`,
    cacheTime: 1000 * 60 * 10, // 10 minutes
    // Decide from the parameters the request was made with, not the current
    // URL: a filter can be cleared while its request is still in flight, and
    // its results must then not end up stored as the suggested posts.
    onSuccess: (data, [search, searchedTags]) => {
      if (!search && !searchedTags) {
        // Store suggested posts in the store so Home and Info share the same list
        // and we avoid refetching when the user hasn't visited Home first (e.g.
        // after reload on another page). Backend shuffles; keeping one list per
        // session preserves order and scroll position when navigating back from an article.
        dispatch(contentActions.setSuggestedPosts(data));
      }
      if (search || searchedTags) {
        const usedTag = tags?.find(tag => tag.id.toString() === searchedTags);
        posthog.capture('info.search_performed', {
          search_query: search || undefined,
          search_tag_id: usedTag?.id.toString(),
          search_tag_slug: usedTag?.slug,
          tag_rank: searchedTags ? tags?.findIndex(tag => tag.id === usedTag?.id) : undefined,
          search_results_count: data?.length,
        });
      }
    },
  });

  const displayedPosts = searchParams.get('search') || searchParams.get('tags') ? searchResults : suggestedPosts;

  // Placeholder for a future grid view implementation.
  // Keeping the prop and a minimal usage avoids breaking existing callers.
  if (allowGridView) {
    // For now, fall back to the default list view.
  }

  const triggerSearch = (search: string) => {
    setSearchParams(prev => {
      const params = createSearchParams(prev);
      if (search) {
        params.set('search', search);
      } else {
        params.delete('search');
      }
      return params;
    });
    if (Platform.isNative()) {
      Keyboard.hide();
    }
  };

  const clearSearch = () => {
    setSearchValue('');
    triggerSearch('');
  };

  const isTagSelected = (tag: string) => {
    return searchParams.get('tags') === tag;
  };

  const toggleTag = (tag: string) => {
    if (isTagSelected(tag)) {
      setSearchParams(prev => {
        const params = createSearchParams(prev);
        params.delete('tags');
        return params;
      });
    } else {
      setSearchParams(prev => {
        const params = createSearchParams(prev);
        params.set('tags', tag);
        return params;
      });
    }
  };

  useEffect(() => {
    if (searchParams.get('search') || searchParams.get('tags') || !suggestedPosts) {
      searchPosts(searchParams.get('search') || undefined, searchParams.get('tags') || undefined);
    }
  }, [searchParams, suggestedPosts, searchPosts]);

  useEffect(() => {
    // scroll tag into view
    if (searchParams.get('tags')) {
      const tagElement = document.querySelector(`#tag-${searchParams.get('tags')}`);
      if (tagElement) {
        tagElement.scrollIntoView({ behavior: 'auto', inline: 'center' });
      }
    }
  }, [searchParams, tags, searchPosts]);

  const postsSkeleton = (
    <div className="flex w-full flex-col gap-4 pt-4">
      <Skeleton className="h-16 w-full" />
      <Skeleton className="h-16 w-full" />
      <Skeleton className="h-16 w-full" />
    </div>
  );

  return (
    <div className="pb-4">
      {isLoadingRecentlyReadPosts && recentlyReadPosts === undefined ? (
        <div className="flex flex-col gap-3 pb-4">
          <Skeleton className="h-5 w-2/5" />
          <Skeleton className="h-4 w-full" />
          <Skeleton className="h-4 w-full" />
          <Skeleton className="h-4 w-3/5" />
        </div>
      ) : (
        (recentlyReadPosts?.length ?? 0) > 0 && (
          <>
            <h3 className="text-subtle-foreground mb-3">{t('infoPage.recentlyRead')}</h3>
            <div className="mb-4 flex w-full flex-row gap-4 overflow-x-scroll pr-8 pb-2 [scrollbar-width:none] mask-r-from-80% mask-r-to-100%">
              {recentlyReadPosts?.map((post, index) => (
                <div
                  // The radius matches the search bar and the article list rows below, not its own one-off value.
                  className="bg-card ease-[cubic-bezier(0.16,1,0.3,1)] fill-mode-forwards animate-in fade-in slide-in-from-left h-24 w-28 shrink-0 overflow-hidden rounded-lg p-3 shadow-xs duration-500"
                  key={post.id}
                  data-testid={`recentpost-${index}`}
                  onClick={() => onArticleSelected(post, index, navigate, dispatch, { opened_on_page: 'info-recents' })}
                  onKeyDown={e => {
                    if (e.key === 'Enter') {
                      onArticleSelected(post, index, navigate, dispatch, { opened_on_page: 'info-recents' });
                    }
                  }}
                  role="button"
                  tabIndex={0}
                >
                  <h4 className="text-base mask-b-from-60% mask-b-to-100%">{post.title}</h4>
                </div>
              ))}
            </div>
          </>
        )
      )}
      <div className="relative mt-2 mb-3 w-full">
        <Input
          type="search"
          // room for the clear + search buttons; the native clear button would duplicate ours
          className="pr-20 [&::-webkit-search-cancel-button]:hidden"
          placeholder={t('infoPage.search.placeholder')}
          value={searchValue}
          onChange={e => setSearchValue(e.target.value)}
          onKeyDown={e => {
            if (e.key === 'Enter') {
              triggerSearch(searchValue);
            }
          }}
          autoCorrect="on"
          enterKeyHint="search"
        />
        <div className="absolute inset-y-0 right-1 flex items-center gap-0.5">
          {searchValue && (
            <Button type="button" variant="ghost" size="icon" className="hover:bg-transparent" onClick={clearSearch}>
              <CloseCircleFilled />
            </Button>
          )}
          <Button type="button" variant="ghost" size="icon" onClick={() => triggerSearch(searchValue)}>
            <SearchOutlined />
          </Button>
        </div>
      </div>
      <div className="mb-4 flex w-full flex-row gap-3 overflow-x-scroll pr-12 [scrollbar-width:none] mask-r-from-80% mask-r-to-100%">
        {isLoadingTags ? (
          <Skeleton className="h-9 w-full" />
        ) : (
          tags?.map((tag, index) => (
            <Badge
              key={tag.id}
              id={`tag-${tag.id}`}
              variant={isTagSelected(tag.id.toString()) ? 'default' : 'outline'}
              className="cursor-pointer"
              data-testid={`tag-${index}`}
              onClick={() => toggleTag(tag.id.toString())}
            >
              #{tag.display_name}
            </Badge>
          ))
        )}
      </div>
      <div className="flex w-full">
        {isLoadingSearchResults ? (
          postsSkeleton
        ) : (
          <PostsList
            posts={displayedPosts ?? []}
            limitPosts={limitPosts}
            posthogOnArticleOpenExtras={{
              opened_on_page: 'info',
              ...((searchParams.get('search')?.length ?? 0) > 0 && {
                found_using_search: searchParams.get('search') as string,
              }),
              ...((searchParams.get('tags')?.length ?? 0) > 0 && {
                found_using_tag_id: searchParams.get('tags') as string,
                found_using_tag_slug: tags?.find(tag => tag.id.toString() === searchParams.get('tags'))?.slug,
              }),
            }}
          />
        )}
      </div>
    </div>
  );
}
